// ScheduleBuilder.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import WeekDropDown from './WeekDropDown';
import GeneratedSchedule from './GeneratedSchedule';
import { DAYS } from '../../constants';
import { API_BASE } from './config';

// Schedules store sales as an array; the form wants a day-keyed object
const salesArrayToObject = (arr) => {
    if (!arr || arr.length === 0) return null;
    const out = {};
    arr.forEach(s => {
        out[s.day] = { day: s.dayShiftSales || 0, night: s.nightShiftSales || 0 };
    });
    return out;
};

const ScheduleBuilder = ({ selectedStore }) => {
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [salesData, setSalesData] = useState(null);
    const [generatedSchedule, setGeneratedSchedule] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [savedList, setSavedList] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);
    const [loadedSales, setLoadedSales] = useState(null);
    const [loadKey, setLoadKey] = useState(0);

    const fetchSavedList = async (storeId = selectedStore) => {
        if (!storeId) return;
        try {
            const response = await axios.get(`${API_BASE}/api/schedule/list`, {
                params: { location: storeId }
            });
            setSavedList(response.data);
        } catch (err) {
            console.error('Error loading saved schedules:', err);
        }
    };

    // Reload the saved list and latest schedule whenever the store changes
    useEffect(() => {
        if (!selectedStore) return;

        const load = async () => {
            await fetchSavedList(selectedStore);
            try {
                const response = await axios.get(`${API_BASE}/api/schedule/latest`, {
                    params: { location: selectedStore }
                });
                if (response.data) {
                    setGeneratedSchedule({ schedule: response.data, summary: null });
                    setSelectedId(response.data._id);

                    const start = new Date(response.data.weekStart);
                    const end = new Date(start);
                    end.setDate(end.getDate() + 6);
                    setSelectedWeek({ start, end });

                    setLoadedSales(salesArrayToObject(response.data.salesData));
                    setLoadKey(k => k + 1);
                } else {
                    setGeneratedSchedule(null);
                    setSelectedId(null);
                    setSelectedWeek(null);
                    setLoadedSales(null);
                    setLoadKey(k => k + 1);
                }
            } catch (err) {
                console.error('Error loading latest schedule:', err);
            }
        };
        load();
    }, [selectedStore]);

    const handleWeekSelect = (week) => {
        setSelectedWeek(week);
        setLoadedSales(null);
        setLoadKey(k => k + 1);
        setError(null);
    };

    const handleSalesDataChange = (data) => {
        setSalesData(data);
    };

    const handleSelectSaved = async (scheduleId) => {
        try {
            const response = await axios.get(`${API_BASE}/api/schedule/${scheduleId}`);
            setGeneratedSchedule({ schedule: response.data, summary: null });
            setSelectedId(scheduleId);

            const start = new Date(response.data.weekStart);
            const end = new Date(start);
            end.setDate(end.getDate() + 6);
            setSelectedWeek({ start, end });

            setLoadedSales(salesArrayToObject(response.data.salesData));
            setLoadKey(k => k + 1);
        } catch (err) {
            console.error('Error loading schedule:', err);
            setError('Could not load that schedule.');
        }
    };

    const isPastWeek = (weekStart) => {
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 7);
        return weekEnd < new Date();
    };

    const handleDeleteSaved = async (scheduleId) => {
        try {
            await axios.delete(`${API_BASE}/api/schedule/${scheduleId}`);

            // If we just deleted what was on screen, fall back to the latest
            if (scheduleId === selectedId) {
                const latest = await axios.get(`${API_BASE}/api/schedule/latest`, {
                    params: { location: selectedStore }
                });
                if (latest.data) {
                    setGeneratedSchedule({ schedule: latest.data, summary: null });
                    setSelectedId(latest.data._id);
                } else {
                    setGeneratedSchedule(null);
                    setSelectedId(null);
                }
            }

            fetchSavedList(selectedStore);
        } catch (err) {
            console.error('Error deleting schedule:', err);
            setError(err.response?.data?.message || 'Could not delete that schedule.');
        } finally {
            setConfirmDelete(null);
        }
    };

    const handleSaveAsVersion = async () => {
        if (!generatedSchedule?.schedule?._id) return;
        try {
            const response = await axios.post(
                `${API_BASE}/api/schedule/${generatedSchedule.schedule._id}/duplicate`
            );
            setGeneratedSchedule({ schedule: response.data, summary: null });
            setSelectedId(response.data._id);
            fetchSavedList(selectedStore);
        } catch (err) {
            console.error('Error saving version:', err);
            setError('Could not save a new version.');
        }
    };

    const handleGenerateSchedule = async () => {
        if (!selectedStore || !selectedWeek || !salesData) {
            setError('Please select a store and week, and enter sales data');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${API_BASE}/api/schedule/generate-schedule`, {
                weekStart: selectedWeek.start,
                scheduleRequirements: {}, // Empty - using default minimums
                salesData,
                location: selectedStore
            });

            setGeneratedSchedule(response.data);
            setSelectedId(response.data.schedule._id);
            fetchSavedList(selectedStore);
        } catch (err) {
            console.error('Error generating schedule:', err);
            setError(err.response?.data?.message || 'Error generating schedule. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleExportToSheets = async () => {
        try {
            const response = await axios.post(`${API_BASE}/api/export/schedule-to-sheets`, {
                schedule: generatedSchedule.schedule,
                salesData: salesData,
                weekStart: selectedWeek.start.toISOString().split('T')[0]
            });

            // Open in new tab
            window.open(response.data.url, '_blank');
        } catch (error) {
            console.error('Error exporting to Google Sheets:', error);
            alert('Error exporting to Google Sheets');
        }
    };

    // Group the saved list by week, preserving newest-first order
    const weekGroups = [];
    savedList.forEach(item => {
        const key = item.weekStart;
        let group = weekGroups.find(g => g.weekStart === key);
        if (!group) {
            group = { weekStart: key, items: [] };
            weekGroups.push(group);
        }
        group.items.push(item);
    });

    return (
        <div className="schedule-builder">
            {confirmDelete && (
                <div className="modal-overlay">
                    <div className="modal sm">
                        <h3>Confirm Delete</h3>
                        <p>
                            Delete this version from the week of{' '}
                            <strong>{new Date(confirmDelete.weekStart).toLocaleDateString()}</strong>?
                            This cannot be undone.
                        </p>
                        <div className="modal-buttons">
                            <button className="cxlbtn" onClick={() => setConfirmDelete(null)}>Cancel</button>
                            <button onClick={() => handleDeleteSaved(confirmDelete._id)}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            <section className="week-selection">
                <h2>1. Schedule for week of: </h2>            
                <WeekDropDown onWeekChange={handleWeekSelect} value={selectedWeek?.start} />
            </section>

            {selectedWeek && (
                <section className="scheduling-requirements">
                    <h2>2. Sales Projections</h2>
                    <SalesDataInput 
                        key={loadKey}
                        initialData={loadedSales}
                        selectedWeek={selectedWeek}
                        onChange={handleSalesDataChange}
                    />

                    {error && <div className="error-message">{error}</div>}

                    <h2>3. </h2><button
                        onClick={handleGenerateSchedule}
                        disabled={!selectedWeek || !salesData || isLoading}
                        className="btn-lg nolm"
                    >
                        {isLoading ? 'Creating...' : 'Create Schedule'}
                    </button>
                    {generatedSchedule && (
                        <button className="savebtn btn-lg" onClick={handleSaveAsVersion}>
                            Save Schedule
                        </button>
                    )}

                    {generatedSchedule && (
                        <a href={`${API_BASE}/api/excel/schedule/${generatedSchedule.schedule._id}`}
                            className="sort"
                        >
                            Download Excel
                        </a>
                    )}
                </section>
            )}

            {generatedSchedule && (
                <section className="generated-schedule">
                    <GeneratedSchedule 
                        schedule={generatedSchedule.schedule}
                        summary={generatedSchedule.summary}
                        selectedStore={selectedStore}
                        onScheduleUpdate={(updated) => {
                            setGeneratedSchedule({ ...generatedSchedule, schedule: updated });
                            fetchSavedList(selectedStore);
                        }}
                    />
                </section>
            )} 

            {weekGroups.length > 0 && (
                <section className="saved-schedules">
                    <h3>Saved Schedules</h3>
                    {weekGroups.map(group => (
                        <details key={group.weekStart}>
                            <summary>
                                Week of {new Date(group.weekStart).toLocaleDateString()}
                                {' '}({group.items.length})
                            </summary>
                            <ul className="revision-list">
                                {group.items.map((item, index) => (
                                    <li
                                        key={item._id}
                                        className={item._id === selectedId ? 'revision selected' : 'revision'}
                                    >
                                        <span onClick={() => handleSelectSaved(item._id)} style={{ cursor: 'pointer' }}>
                                            Created {new Date(item.createdAt).toLocaleDateString()}
                                            {' '}{new Date(item.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                        </span>
                                        {(group.items.length > 1 || isPastWeek(group.weekStart)) && (
                                            <button
                                                className="revision-delete"
                                                onClick={() => setConfirmDelete(item)}
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </details>
                    ))}
                </section>
            )} 

            {generatedSchedule && (
                <button className="savebtn btn-lg" onClick={handleSaveAsVersion}>
                    Save Schedule
                </button>
            )}

            {generatedSchedule && (
                <a href={`${API_BASE}/api/excel/schedule/${generatedSchedule.schedule._id}`}
                    className="sort"
                >
                    Download Excel
                </a>
            )}

        </div>
    );
};

const SalesDataInput = ({ selectedWeek, initialData, onChange }) => {
    const [salesData, setSalesData] = useState(initialData || {
        Monday: { day: 1100, night: 2200 },
        Tuesday: { day: 1300, night: 2400 },
        Wednesday: { day: 1500, night: 3200 },
        Thursday: { day: 1600, night: 3450 },
        Friday: { day: 1675, night: 6400 },
        Saturday: { day: 1600, night: 5400 },
        Sunday: { day: 1800, night: 3900 }
    });

    // Call onChange with initial values when component mounts
    useEffect(() => {
        onChange(salesData);
    }, []); 

    const handleSalesChange = (day, shiftType, value) => {
        const updated = {
            ...salesData,
            [day]: {
                ...salesData[day],
                [shiftType]: parseFloat(value) || 0
            }
        };
        setSalesData(updated);
        onChange(updated);
    };

    return (
        <div className="sales-data-grid">
            {DAYS.map(day => (
                <div key={day} className="sales-row">
                    <div>{day}</div>
                    <div>
                        <label>Day Sales
                        <input
                            value={salesData[day].day}
                            onChange={(e) => handleSalesChange(day, 'day', e.target.value)}
                            placeholder="$0"
                        />
                        </label>
                    </div>
                    <div>
                        <label>Night Sales
                        <input
                            value={salesData[day].night}
                            onChange={(e) => handleSalesChange(day, 'night', e.target.value)}
                            placeholder="$0"
                        />
                        </label>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default ScheduleBuilder;